import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    effect,
    ElementRef,
    inject,
    input,
    OnDestroy,
    signal,
} from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';

const ANIMATION_DURATION_MS = 300;
const ANIMATION_FALLBACK_MS = ANIMATION_DURATION_MS + 50;

// note: inorder to display content correctly, you must use fi-expandable-text-mixin()
@Directive({
    selector: '[fiExpandableText]',
    standalone: false,
    host: {
        class: 'fi-expandable-text',
        '[class.fi-expandable-text--clamped]': '!expanded()',
        '[class.fi-expandable-text--expanded]': 'expanded()',
        '[class.fi-expandable-text--toggle-disabled]': 'isToggleDisabled()',
    },
})
export class FiExpandableTextDirective implements AfterViewInit, OnDestroy {
    public fiExpandableText = input<number>(2);
    public isToggleDisabled = input<boolean>(false);

    private readonly host = inject(ElementRef<HTMLElement>).nativeElement;
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly appTextService = inject(AppTextService);

    private resizeObserver: ResizeObserver | null = null;
    private toggleContainer: HTMLElement | null = null;
    private toggleButton: HTMLButtonElement | null = null;
    private ellipsisEl: HTMLElement | null = null;
    private lineHeight = 0;
    private animating = false;

    protected expanded = signal(false);
    private needsTruncation = false;
    private viewInitialized = false;

    constructor() {
        effect(() => {
            this.fiExpandableText();
            this.isToggleDisabled();
            if (!this.viewInitialized || this.animating) {
                return;
            }
            this.syncClampToLineCount();
            this.syncToggleVisibility();
        });
    }

    public ngAfterViewInit(): void {
        this.lineHeight = this.computeLineHeight();
        this.host.style.position = 'relative';
        this.applyClamp();
        this.checkOverflow();
        this.setupResizeObserver();
        this.viewInitialized = true;
    }

    public ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    private computeLineHeight(): number {
        return parseFloat(getComputedStyle(this.host).lineHeight) || 20;
    }

    private getClampedMaxHeight(): number {
        return this.lineHeight * this.fiExpandableText();
    }

    private applyClamp(): void {
        this.host.style.setProperty('--fi-expandable-text-line-count', `${this.fiExpandableText()}`);
        this.host.style.maxHeight = `${this.getClampedMaxHeight()}px`;
        this.host.style.overflow = 'hidden';
    }

    private syncClampToLineCount(): void {
        this.lineHeight = this.computeLineHeight();
        if (this.expanded()) {
            return;
        }
        this.applyClamp();
        this.checkOverflow();
        this.cdr.markForCheck();
    }

    private removeClamp(): void {
        this.host.style.maxHeight = '';
        this.host.style.overflow = '';
    }

    private checkOverflow(): void {
        const isOverflowing = this.host.scrollHeight > this.host.clientHeight;

        if (isOverflowing && !this.needsTruncation) {
            this.needsTruncation = true;
            this.syncToggleVisibility();
        } else if (!isOverflowing && !this.expanded() && this.needsTruncation) {
            this.needsTruncation = false;
            this.syncToggleVisibility();
        }
    }

    private createToggle(): void {
        if (this.toggleContainer) {
            return;
        }

        this.toggleContainer = document.createElement('span');
        this.toggleContainer.className =
            'fi-expandable-text__toggle-container fi-expandable-text__toggle-container--clamped';

        this.ellipsisEl = document.createElement('span');
        this.ellipsisEl.className = 'fi-expandable-text__ellipsis';
        this.ellipsisEl.textContent = '...';
        this.toggleContainer.appendChild(this.ellipsisEl);

        this.syncToggleButtonVisibility();

        this.host.appendChild(this.toggleContainer);
    }

    private removeToggle(): void {
        if (!this.toggleContainer) {
            return;
        }

        this.toggleContainer.remove();
        this.toggleContainer = null;
        this.toggleButton = null;
        this.ellipsisEl = null;
    }

    private updateButtonText(): void {
        if (!this.toggleButton) {
            return;
        }

        const key = this.expanded() ? 'common.show_less' : 'common.show_more';
        this.toggleButton.textContent = this.appTextService.get(key);
    }

    private syncToggleButtonVisibility(): void {
        if (!this.toggleContainer) {
            return;
        }

        if (this.isToggleDisabled()) {
            this.toggleButton?.remove();
            this.toggleButton = null;
            return;
        }

        if (this.toggleButton) {
            this.updateButtonText();
            return;
        }

        this.toggleButton = document.createElement('button');
        this.toggleButton.className = 'fi-expandable-text__toggle';
        this.updateButtonText();
        this.toggleButton.addEventListener('click', (event: MouseEvent) => {
            event.stopPropagation();
            this.toggle();
        });
        this.toggleContainer.appendChild(this.toggleButton);
    }

    private syncToggleVisibility(): void {
        if (!this.needsTruncation || this.isToggleDisabled()) {
            this.removeToggle();
            return;
        }

        this.createToggle();
    }

    private setToggleStyle(collapsed: boolean): void {
        if (!this.toggleContainer || !this.ellipsisEl) {
            return;
        }

        this.toggleContainer.classList.toggle('fi-expandable-text__toggle-container--clamped', collapsed);
        this.toggleContainer.classList.toggle('fi-expandable-text__toggle-container--expanded', !collapsed);
        this.ellipsisEl.style.display = collapsed ? '' : 'none';
    }

    private toggle(): void {
        if (this.expanded()) {
            this.collapse();
        } else {
            this.expand();
        }
    }

    private expand(): void {
        const collapsedHeight = this.host.clientHeight;
        const fullScrollHeight = this.host.scrollHeight;

        this.removeClamp();
        this.setToggleStyle(false);
        this.expanded.set(true);
        this.updateButtonText();

        this.animateHeight(collapsedHeight, this.host.scrollHeight || fullScrollHeight, () => {
            this.host.style.overflow = '';
        });
    }

    private collapse(): void {
        const expandedHeight = this.host.scrollHeight;

        this.setToggleStyle(true);
        this.expanded.set(false);
        this.updateButtonText();

        this.animateHeight(expandedHeight, this.getClampedMaxHeight(), () => {
            this.applyClamp();
        });
    }

    private animateHeight(from: number, to: number, onComplete: () => void): void {
        this.animating = true;
        let settled = false;

        const cleanup = () => {
            if (settled) {
                return;
            }
            settled = true;
            this.animating = false;
            this.host.style.height = '';
            this.host.style.transition = '';
            onComplete();
        };

        this.host.style.height = `${from}px`;
        this.host.style.transition = `height ${ANIMATION_DURATION_MS}ms ease`;
        this.host.style.overflow = 'hidden';

        requestAnimationFrame(() => {
            this.host.style.height = `${to}px`;
            this.host.addEventListener('transitionend', cleanup, { once: true });
            setTimeout(cleanup, ANIMATION_FALLBACK_MS);
        });
    }

    private setupResizeObserver(): void {
        this.resizeObserver = new ResizeObserver(() => {
            if (!this.expanded() && !this.animating) {
                this.checkOverflow();
                this.cdr.markForCheck();
            }
        });
        this.resizeObserver.observe(this.host);
    }
}
